import React, { useState, useEffect } from "react";
import { Filter, X, MapPin, Euro, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EventFilters = ({ onFiltersChange, activeFiltersCount = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    city: "",
    minPrice: "",
    maxPrice: "",
    dateFrom: "",
    dateTo: "",
    hideSoldOut: false,
  });

  // Fetch available locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API}/events/locations`);
        setLocations(response.data || []);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    const activeFilters = {};
    
    if (filters.city) activeFilters.city = filters.city;
    if (filters.minPrice) activeFilters.min_price = parseFloat(filters.minPrice);
    if (filters.maxPrice) activeFilters.max_price = parseFloat(filters.maxPrice);
    if (filters.dateFrom) activeFilters.date_from = filters.dateFrom;
    if (filters.dateTo) activeFilters.date_to = filters.dateTo;
    if (filters.hideSoldOut) activeFilters.hide_sold_out = true;
    
    onFiltersChange(activeFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      city: "",
      minPrice: "",
      maxPrice: "",
      dateFrom: "",
      dateTo: "",
      hideSoldOut: false,
    };
    setFilters(clearedFilters);
    onFiltersChange({});
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== "" && v !== false);

  return (
    <div className="w-full">
      {/* Toggle Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full sm:w-auto flex items-center gap-2 border-border/50 bg-card/50"
        data-testid="filters-toggle-btn"
      >
        <Filter className="w-4 h-4" />
        <span>Filtres</span>
        {activeFiltersCount > 0 && (
          <span className="bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
            {activeFiltersCount}
          </span>
        )}
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 ml-1" />
        ) : (
          <ChevronDown className="w-4 h-4 ml-1" />
        )}
      </Button>

      {/* Expanded Filters Panel */}
      {isExpanded && (
        <div 
          className="mt-3 p-4 bg-card/80 backdrop-blur-sm rounded-lg border border-border/50 space-y-4"
          data-testid="filters-panel"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* City Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Ville
              </Label>
              <Select
                value={filters.city}
                onValueChange={(value) => handleFilterChange("city", value === "all" ? "" : value)}
              >
                <SelectTrigger data-testid="filter-city">
                  <SelectValue placeholder="Toutes les villes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les villes</SelectItem>
                  {locations.map((location) => (
                    <SelectItem key={location} value={location}>
                      {location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price Range */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <Euro className="w-3 h-3" />
                Prix (€)
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.minPrice}
                  onChange={(e) => handleFilterChange("minPrice", e.target.value)}
                  className="w-1/2"
                  data-testid="filter-min-price"
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.maxPrice}
                  onChange={(e) => handleFilterChange("maxPrice", e.target.value)}
                  className="w-1/2"
                  data-testid="filter-max-price"
                />
              </div>
            </div>

            {/* Date From */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                À partir du
              </Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                data-testid="filter-date-from"
              />
            </div>

            {/* Date To */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Jusqu'au
              </Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                data-testid="filter-date-to"
              />
            </div>
          </div>

          {/* Hide Sold Out Toggle */}
          <div className="flex items-center justify-between pt-2 border-t border-border/30">
            <div className="flex items-center gap-3">
              <Switch
                id="hide-sold-out"
                checked={filters.hideSoldOut}
                onCheckedChange={(checked) => handleFilterChange("hideSoldOut", checked)}
                data-testid="filter-hide-sold-out"
              />
              <Label htmlFor="hide-sold-out" className="text-sm cursor-pointer">
                Masquer les événements complets
              </Label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Effacer
                </Button>
              )}
              <Button
                size="sm"
                onClick={applyFilters}
                data-testid="apply-filters-btn"
              >
                Appliquer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
